import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, User as UserIcon, Mail, Phone, Calendar, Pencil, LogOut, Trash2, Check, X, ShoppingBag, BookOpen, Bell } from 'lucide-react';
import { AuthContext } from '../../authContext';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;

  &:hover { color: #0f172a; }
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1.5rem;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  overflow: hidden;
`;

const ProfileCard = styled(Card)`
  padding: 1.75rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Avatar = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0f172a 0%, #334155 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
`;

const ProfileName = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 0.25rem;
`;

const ProfileId = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0 0 1.25rem;
`;

const ProfileMeta = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.875rem;
  color: #475569;
  text-align: left;

  svg { color: #94a3b8; flex-shrink: 0; }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const QuickLinks = styled.nav`
  margin-top: 1.25rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const QuickLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.5rem;
  color: #334155;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: background 0.15s;

  &:hover { background: #f1f5f9; color: #0f172a; }
  svg { color: #64748b; }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
`;

const SectionTitle = styled.h2`
  font-size: 1.05rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
`;

const SectionBody = styled.div`
  padding: 1.5rem;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  ${(props) => props.$full && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 500;
  color: #64748b;
`;

const ReadValue = styled.div`
  font-size: 0.95rem;
  color: #0f172a;
  padding: 0.5rem 0;
  font-weight: 500;
  min-height: 1.5rem;
`;

const baseInputCss = `
  width: 100%;
  font-size: 0.95rem;
  color: #0f172a;
  padding: 0.55rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  background: white;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    outline: none;
    border-color: #0f172a;
    box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.08);
  }

  &:disabled {
    background: #f1f5f9;
    color: #64748b;
    cursor: not-allowed;
  }
`;

const Input = styled.input`${baseInputCss}`;
const Select = styled.select`${baseInputCss}`;

const BirthRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const RadioOption = styled.label`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.55rem 0.75rem;
  border: 1px solid ${(props) => (props.$checked ? '#0f172a' : '#cbd5e1')};
  background: ${(props) => (props.$checked ? '#0f172a' : 'white')};
  color: ${(props) => (props.$checked ? 'white' : '#334155')};
  border-radius: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  input { display: none; }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  justify-content: flex-end;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;

  ${(props) => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #0f172a;
          color: white;
          &:hover { background: #1e293b; }
          &:disabled { background: #94a3b8; cursor: not-allowed; }
        `;
      case 'danger':
        return `
          background: white;
          color: #ef4444;
          border-color: #fecaca;
          &:hover { background: #fef2f2; border-color: #ef4444; }
        `;
      default:
        return `
          background: white;
          color: #0f172a;
          border-color: #cbd5e1;
          &:hover { background: #f1f5f9; }
        `;
    }
  }}
`;

const Toast = styled.div`
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  background: ${(props) => (props.$kind === 'error' ? '#ef4444' : '#0f172a')};
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.18);
  z-index: 100;
`;

const LoginPrompt = styled.div`
  max-width: 480px;
  margin: 4rem auto;
  padding: 2.5rem 2rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  text-align: center;

  h2 { color: #0f172a; margin: 0 0 0.5rem; }
  p { color: #64748b; margin: 0 0 1.5rem; }
`;

const splitBirth = (birth) => {
  const s = (birth || '').toString();
  if (s.length >= 8) {
    return { birth_y: s.slice(0, 4), birth_m: s.slice(4, 6), birth_d: s.slice(6, 8) };
  }
  return { birth_y: '', birth_m: '', birth_d: '' };
};

const MyPage = () => {
  const { isLoggedIn, user, logout, refreshProfile } = useContext(AuthContext);
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, kind = 'success') => {
    setToast({ message, kind });
    setTimeout(() => setToast(null), 2400);
  };

  // 페이지 진입 시 프로필 최신화 (페이지 새로고침 대응)
  useEffect(() => {
    if (isLoggedIn && user && !user.email && refreshProfile) {
      refreshProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const initial = useMemo(() => {
    const { birth_y, birth_m, birth_d } = splitBirth(user?.birth);
    return {
      id: user?.id || '',
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      gender: user?.gender || '',
      alias: user?.alias || '',
      birth_y,
      birth_m,
      birth_d
    };
  }, [user]);

  const handleEdit = () => {
    setForm(initial);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setForm({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/UpdateProfile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_id: form.id,
          user_name: form.name,
          gender: form.gender,
          birth_y: form.birth_y,
          birth_m: form.birth_m,
          birth_d: form.birth_d,
          email: form.email,
          phone: form.phone,
          alias: form.alias
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || '프로필 수정에 실패했습니다.');
      }
      await refreshProfile();
      setIsEditing(false);
      showToast('프로필이 저장되었습니다.');
    } catch (error) {
      console.error('프로필 수정 실패:', error);
      showToast(error.message || '프로필 수정에 실패했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDelete = async () => {
    if (!window.confirm('정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
    try {
      const response = await fetch(`/api/DeleteProfile/${encodeURIComponent(initial.id)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) throw new Error(await response.text());
      showToast('계정이 삭제되었습니다.');
      await logout();
      navigate('/');
    } catch (error) {
      console.error('계정 삭제 실패:', error);
      showToast('계정 삭제에 실패했습니다.', 'error');
    }
  };

  if (!isLoggedIn) {
    return (
      <Container>
        <LoginPrompt>
          <h2>로그인이 필요합니다</h2>
          <p>마이페이지를 이용하려면 먼저 로그인해 주세요.</p>
          <Button $variant="primary" onClick={() => navigate('/Login')}>
            로그인하러 가기
          </Button>
        </LoginPrompt>
      </Container>
    );
  }

  const view = isEditing ? form : initial;
  const avatarChar = (view.name || view.id || 'A').toString().trim().charAt(0).toUpperCase();
  const genderLabel = view.gender === '1' ? '남성' : view.gender === '2' ? '여성' : '미설정';
  const birthLabel = [initial.birth_y, initial.birth_m, initial.birth_d].filter(Boolean).join('. ') || '미설정';

  return (
    <Container>
      <TopBar>
        <BackButton to="/">
          <ArrowLeft size={20} />
          쇼핑 계속하기
        </BackButton>
        <PageTitle>마이페이지</PageTitle>
      </TopBar>

      <Layout>
        <ProfileCard>
          <Avatar>{avatarChar}</Avatar>
          <ProfileName>{initial.name || '회원'}</ProfileName>
          <ProfileId>@{initial.id}</ProfileId>

          <ProfileMeta>
            <MetaRow><Mail size={16} /><span>{initial.email || '이메일 미등록'}</span></MetaRow>
            <MetaRow><Phone size={16} /><span>{initial.phone || '전화번호 미등록'}</span></MetaRow>
            <MetaRow><Calendar size={16} /><span>{birthLabel}</span></MetaRow>
          </ProfileMeta>

          <QuickLinks>
            <QuickLink to="/Cart"><ShoppingBag size={16} />장바구니</QuickLink>
            <QuickLink to="/course-library/1"><BookOpen size={16} />강의 둘러보기</QuickLink>
          </QuickLinks>
        </ProfileCard>

        <Card>
          <SectionHeader>
            <SectionTitle>내 정보</SectionTitle>
            {!isEditing && (
              <Button onClick={handleEdit}>
                <Pencil size={14} />수정
              </Button>
            )}
          </SectionHeader>

          <SectionBody>
            <FieldGrid>
              <Field>
                <Label>아이디</Label>
                <Input value={view.id} disabled />
              </Field>

              <Field>
                <Label>이름</Label>
                {isEditing ? (
                  <Input name="name" value={view.name || ''} onChange={handleChange} />
                ) : (
                  <ReadValue>{view.name || '-'}</ReadValue>
                )}
              </Field>

              <Field>
                <Label>이메일</Label>
                {isEditing ? (
                  <Input type="email" name="email" value={view.email || ''} onChange={handleChange} />
                ) : (
                  <ReadValue>{view.email || '-'}</ReadValue>
                )}
              </Field>

              <Field>
                <Label>전화번호</Label>
                {isEditing ? (
                  <Input name="phone" value={view.phone || ''} onChange={handleChange} placeholder="010-0000-0000" />
                ) : (
                  <ReadValue>{view.phone || '-'}</ReadValue>
                )}
              </Field>

              <Field $full>
                <Label>생년월일</Label>
                {isEditing ? (
                  <BirthRow>
                    <Input name="birth_y" value={view.birth_y || ''} onChange={handleChange} placeholder="YYYY" maxLength={4} />
                    <Select name="birth_m" value={view.birth_m || ''} onChange={handleChange}>
                      <option value="">월</option>
                      {Array.from({ length: 12 }, (_, i) => {
                        const v = String(i + 1).padStart(2, '0');
                        return <option key={v} value={v}>{i + 1}월</option>;
                      })}
                    </Select>
                    <Input name="birth_d" value={view.birth_d || ''} onChange={handleChange} placeholder="DD" maxLength={2} />
                  </BirthRow>
                ) : (
                  <ReadValue>{birthLabel}</ReadValue>
                )}
              </Field>

              <Field $full>
                <Label>성별</Label>
                {isEditing ? (
                  <RadioGroup>
                    <RadioOption $checked={view.gender === '1'}>
                      <input type="radio" name="gender" value="1" checked={view.gender === '1'} onChange={handleChange} />
                      남성
                    </RadioOption>
                    <RadioOption $checked={view.gender === '2'}>
                      <input type="radio" name="gender" value="2" checked={view.gender === '2'} onChange={handleChange} />
                      여성
                    </RadioOption>
                  </RadioGroup>
                ) : (
                  <ReadValue>{genderLabel}</ReadValue>
                )}
              </Field>

              {(isEditing || initial.alias) && (
                <Field $full>
                  <Label>닉네임</Label>
                  {isEditing ? (
                    <Input name="alias" value={view.alias || ''} onChange={handleChange} placeholder="다른 사용자에게 보일 이름" />
                  ) : (
                    <ReadValue>{view.alias || '-'}</ReadValue>
                  )}
                </Field>
              )}
            </FieldGrid>
          </SectionBody>

          <Actions>
            {isEditing ? (
              <>
                <Button onClick={handleCancel} disabled={saving}>
                  <X size={14} />취소
                </Button>
                <Button $variant="primary" onClick={handleSave} disabled={saving}>
                  <Check size={14} />{saving ? '저장 중...' : '저장'}
                </Button>
              </>
            ) : (
              <>
                <Button $variant="danger" onClick={handleDelete}>
                  <Trash2 size={14} />회원 탈퇴
                </Button>
                <Button onClick={handleLogout}>
                  <LogOut size={14} />로그아웃
                </Button>
              </>
            )}
          </Actions>
        </Card>
      </Layout>

      {toast && <Toast $kind={toast.kind}>{toast.message}</Toast>}
    </Container>
  );
};

export default MyPage;
